import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { errorToastMessage } from '../../../../components/layout/toast-root/toast';
import { useFileList } from '../../../../hooks/use-file-list';
import { useGetFilePaths } from '../../../../redux/api/appConfigApi';
import type {
  IPostsViewComment,
  IPostsViewPost,
} from '../../../../redux/api/posts/posts-api.types';
import {
  useAddPostReaction,
  useDeletePost,
  usePostPutPost,
  usePostPutPostComment,
} from '../../../../redux/api/postsApi';
import { useGetMyDetails } from '../../../../redux/api/users/usersApi';
import { FileVisibility } from '../../../../shared/constants';
import { IAppFile } from '../../../../types/interfaces/app-file';
import { IComment } from '../../../../types/interfaces/comment';
import { IPost } from '../../../../types/interfaces/post';
import {
  deleteFilesFromFirebase,
  uploadFileAndCreateAppFile,
} from '../../../../utils/files/firebase-storage-utils';

const showCommentByDefault = 3;
const showMoreCommentsCount = 5;

export function usePost(post: IPostsViewPost, refreshPosts?: () => void) {
  const { t } = useTranslation();
  const [addPostReaction] = useAddPostReaction();
  const [postPutPostComment] = usePostPutPostComment();
  const [postPutPost] = usePostPutPost();
  const [deletePost] = useDeletePost();
  const { data: user } = useGetMyDetails();
  const { data: filePaths } = useGetFilePaths();

  const {
    fileIdsToDelete,
    filesToUpload,
    resultFiles: postFiles,
    onDeleteFile,
    onUploadFiles,
    resetFilesState,
  } = useFileList(post.files);

  const {
    filesToUpload: commentFilesToUpload,
    onDeleteFile: onDeleteCommentFile,
    onUploadFiles: onUploadCommentFiles,
    resetFilesState: resetCommentFilesState,
  } = useFileList([]);

  const [isSavingComment, setIsSavingComment] = useState(false);
  const [isSavingPost, setIsSavingPost] = useState(false);
  const [isDeletingPost, setIsDeletingPost] = useState(false);
  const [editedPost, setEditedPost] = useState<IPostsViewPost | null>(null);
  const [newComment, setNewComment] = useState<IComment>({
    content: '',
    postId: post.id,
    parent: null,
  });
  const [userReaction, setUserReaction] = useState<boolean>(null);
  const [showedCommentsCount, setShowedCommentsCount] =
    useState(showCommentByDefault);

  useEffect(() => {
    if (userReaction === null) {
      setUserReaction(
        !!post.reactions?.find((reaction) => reaction.userId === user.id),
      );
    }
  }, [post]);

  function onToggleToEditPost() {
    setEditedPost(post);
  }

  function onPostEdit(newValue: string) {
    setEditedPost({ ...post, content: newValue });
  }

  function onCommentEdit(newValue: string) {
    setNewComment({ ...newComment, content: newValue });
  }

  function onCommentReply(comment: IPostsViewComment) {
    setNewComment({
      ...newComment,
      parent: comment as IComment,
      parentId: comment.id,
    });
  }

  function onCommentDeleteFromReply() {
    setNewComment({ ...newComment, parent: null, parentId: null });
  }

  async function onCommentSave() {
    if (!user || !filePaths || !user.uid || !user.id) {
      return;
    }

    setIsSavingComment(true);
    const uploadedStoragePaths: string[] = [];
    let hasError = false;

    try {
      const uploadedFiles: IAppFile[] = await Promise.all(
        commentFilesToUpload.map(async (file) => {
          const uploadedFile = await uploadFileAndCreateAppFile(
            file,
            filePaths.posts,
            user.uid,
            user.id,
            FileVisibility.public,
          );

          uploadedStoragePaths.push(uploadedFile.storageLink);
          if (uploadedFile.previewStorageLink) {
            uploadedStoragePaths.push(uploadedFile.previewStorageLink);
          }
          return uploadedFile;
        }),
      );

      await postPutPostComment({
        postId: post.id,
        body: { comment: newComment, filesToUpload: uploadedFiles },
      }).unwrap();

      refreshPosts && refreshPosts();
      setNewComment({ content: '', postId: post.id, parent: null });
      setShowedCommentsCount((prev) => prev + 1);
      resetCommentFilesState();
    } catch (error) {
      hasError = true;
      console.error(
        'Error uploading files to Firebase Storage or saving comment:',
        error,
      );
    } finally {
      if (hasError && uploadedStoragePaths.length > 0) {
        await deleteFilesFromFirebase(uploadedStoragePaths);
      }
      setIsSavingComment(false);
    }
  }

  function onPostReaction() {
    return addPostReaction({ id: post.id })
      .then(() => setUserReaction((prev) => !prev))
      .catch((e) => console.log(e));
  }

  async function onPostSave() {
    if (!user || !filePaths || !user.uid || !user.id) {
      return;
    }

    setIsSavingPost(true);
    const uploadedStoragePaths: string[] = [];
    let hasError = false;

    try {
      const uploadedFiles: IAppFile[] = await Promise.all(
        filesToUpload.map(async (file) => {
          const uploadedFile = await uploadFileAndCreateAppFile(
            file,
            filePaths.posts,
            user.uid,
            user.id,
            FileVisibility.public,
          );

          uploadedStoragePaths.push(uploadedFile.storageLink);

          return uploadedFile;
        }),
      );

      await postPutPost({
        body: {
          post: editedPost as IPost,
          fileIdsToDelete,
          filesToUpload: uploadedFiles,
        },
        id: post.id,
      }).unwrap();

      refreshPosts && refreshPosts();
      setEditedPost(null);
      resetFilesState();
    } catch (error) {
      hasError = true;
      console.error(
        'Error uploading files to Firebase Storage or saving post:',
        error,
      );
      errorToastMessage(t('messages.uploadOrSaveFailed'));
    } finally {
      if (hasError && uploadedStoragePaths.length > 0) {
        await deleteFilesFromFirebase(uploadedStoragePaths);
      }
      setIsSavingPost(false);
    }
  }

  function onPostDelete() {
    setIsDeletingPost(true);

    return deletePost({ id: post.id })
      .then((response) => {
        if ('error' in response) console.log(response);
        else refreshPosts && refreshPosts();
      })
      .finally(() => {
        setIsDeletingPost(false);
      });
  }

  function onShowMore() {
    setShowedCommentsCount((prev) =>
      prev + showMoreCommentsCount < post.comments.length
        ? prev + showMoreCommentsCount
        : post.comments.length,
    );
  }

  function onPostCancel() {
    resetFilesState();
    setEditedPost(null);
  }

  return {
    onToggleToEditPost,
    onPostEdit,
    onPostSave,
    onPostDelete,
    onPostReaction,
    onCommentSave,
    onCommentEdit,
    onCommentReply,
    onCommentDeleteFromReply,
    onUploadFiles,
    onDeleteFile,
    onDeleteCommentFile,
    onUploadCommentFiles,
    onShowMore,
    onPostCancel,
    editedPost,
    newComment,
    postFiles,
    commentFiles: commentFilesToUpload,
    userReaction,
    user,
    showedCommentsCount,
    isSavingComment,
    isSavingPost,
    isDeletingPost,
  };
}
